import { FilterAssetsModal } from "@features/ax/shared/filterAssetsModal";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@shared/ui/tooltip";
import { AlertTriangle, HelpCircle } from "lucide-react";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

interface CriticalAsset {
  id: string;
  name: string;
  status: string;
  eta: string;
}

export const CriticalAssetsOutOfService: React.FC<{ data: any }> = ({
  data,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const handlePrioritize = (id: string, assetId: string, status: string) => {
    navigate(`/ax/asset-registry/assets/view/${assetId}`);
  };
  const [showCriticalAssetsModal, setShowCriticalAssetsModal] = useState(false);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Assets Out of Service
            </CardTitle>
          </div>
          <div className="mt-1">
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <ul className="list-disc pl-5 space-y-1">
                  <li>
                    <strong>Asset Details:</strong> The specific asset that is
                    currently out of service.
                  </li>
                  <li>
                    <strong>Current Status:</strong> The latest update on the
                    repair or recovery status of this asset.
                  </li>
                  <li>
                    <strong>Estimated Return to Service :</strong> The estimated
                    date this asset is expected to be operational again.
                  </li>
                  <li>
                    <strong>Prioritize Repair:</strong> Click to escalate the
                    priority of the repair work order for this asset.
                  </li>
                </ul>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {data?.activeAlerts?.criticalAssetsOutOfService &&
        data.activeAlerts.criticalAssetsOutOfService.length > 0 ? (
          <>
            <div className="space-y-3 mt-3">
              {data.activeAlerts.criticalAssetsOutOfService.map((asset) => (
                <div
                  key={asset.assetId}
                  className="flex items-center justify-between p-3 bg-white rounded border border-red-200"
                >
                  <div>
                    <p className="font-medium">{asset.assetName || "N/A"}</p>
                    <p className="text-sm text-gray-600">
                      {asset.assetNo || "N/A"}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="secondary">{asset.status || "N/A"}</Badge>
                      <span className="text-xs text-gray-500">
                        ETA: {asset.eta || "N/A"}
                      </span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      handlePrioritize(asset.id, asset.assetId, asset.status)
                    }
                  >
                    View
                  </Button>
                </div>
              ))}
            </div>

            <div className="flex justify-center mt-4">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowCriticalAssetsModal(true)}
              >
                View All
              </Button>
            </div>
          </>
        ) : (
          <div className="p-3 rounded text-center text-gray-500">
            No critical assets out of service.
          </div>
        )}
      </CardContent>

      <FilterAssetsModal
        entityId={id}
        entityType={"network"}
        filterType={"critical_out_of_service"}
        showCriticalAssetsModal={showCriticalAssetsModal}
        setShowCriticalAssetsModal={setShowCriticalAssetsModal}
      />
    </Card>
  );
};
